import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { CartContext } from "./CartContext";
import { formatCurrency } from "../utils/formatters";

const getOptions = (product, canonicalKey, legacyKey) => {
  const options = product?.[canonicalKey] || product?.[legacyKey] || [];
  return Array.isArray(options) ? options.filter(Boolean) : [];
};

const scrollToTop = () => {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

function ProductCard({ product, priority = false }) {
  const { addToCart, isWishlisted, toggleWishlist } = useContext(CartContext);

  if (!product) return null;

  const sizes = getOptions(product, "sizes", "size");
  const colors = getOptions(product, "colors", "colour");
  const primaryImage = product.images?.[0] || product.image;
  const secondaryImage = product.images?.[1];
  const detailPath = `/shopDetails/${product.id}`;
  const soldOut = Number(product.stock) <= 0;
  const hasSingleVariant = sizes.length === 1 && colors.length === 1;
  const saved = isWishlisted(product.id);
  const isOnSale =
    Number(product.compareAtPrice) > 0 &&
    Number(product.compareAtPrice) > Number(product.price);

  const handleQuickAdd = () => {
    if (soldOut || !hasSingleVariant) return;

    addToCart(product, {
      quantity: 1,
      size: sizes[0],
      color: colors[0],
    });
  };

  return (
    <article className="product-card" role="listitem">
      <div className="product-card__media">
        <Link
          className="product-card__image-link"
          to={detailPath}
          onClick={scrollToTop}
          aria-label={`View ${product.name}`}
        >
          <span className="product-card__image-frame">
            <img
              className="product-card__image product-card__image--primary"
              src={primaryImage}
              alt={product.name}
              loading={priority ? "eager" : "lazy"}
              decoding="async"
            />
            {secondaryImage && secondaryImage !== primaryImage && (
              <img
                className="product-card__image product-card__image--secondary"
                src={secondaryImage}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
              />
            )}
          </span>
        </Link>

        {(isOnSale || product.isNew) && (
          <div className="product-card__badges" aria-label="Product status">
            {isOnSale && <span className="product-card__badge">Sale</span>}
            {product.isNew && (
              <span className="product-card__badge product-card__badge--new">
                New
              </span>
            )}
          </div>
        )}

        <button
          className={`product-card__wishlist${saved ? " is-active" : ""}`}
          type="button"
          onClick={() => toggleWishlist(product.id)}
          aria-label={`${saved ? "Remove" : "Save"} ${product.name} ${
            saved ? "from" : "to"
          } favourites`}
          aria-pressed={saved}
        >
          <span aria-hidden="true">{saved ? "♥" : "♡"}</span>
        </button>

        {soldOut ? (
          <button
            className="product-card__quick-add"
            type="button"
            disabled
          >
            Sold out
          </button>
        ) : hasSingleVariant ? (
          <button
            className="product-card__quick-add"
            type="button"
            onClick={handleQuickAdd}
          >
            Quick add
          </button>
        ) : (
          <Link
            className="product-card__quick-add"
            to={detailPath}
            onClick={scrollToTop}
            aria-label={`Choose options for ${product.name}`}
          >
            Choose options
          </Link>
        )}
      </div>

      <div className="product-card__details">
        <div className="product-card__heading">
          <h3 className="product-card__title">
            <Link to={detailPath} onClick={scrollToTop}>
              {product.name}
            </Link>
          </h3>
          <div className="product-card__prices">
            <span className="product-card__price">
              {formatCurrency(product.price)}
            </span>
            {isOnSale && (
              <del className="product-card__compare-price">
                {formatCurrency(product.compareAtPrice)}
              </del>
            )}
          </div>
        </div>
        <p className="product-card__meta">
          {product.category}
          {colors.length > 0 && (
            <span className="product-card__colour-count">
              {` · ${colors.length} ${colors.length === 1 ? "colour" : "colours"}`}
            </span>
          )}
        </p>
      </div>
    </article>
  );
}

export default ProductCard;
